import axios, { AxiosRequestConfig, AxiosResponse } from "axios";


import API_URL from "./apiUri";

type BaseType =
  | "default"

interface APICallParams {
  methodName: string;
  apiUrl: string;
  body?: Record<string, any>; // You might want to specify a more specific type
  params?: Record<string, any>; // Parameters to be sent in the URL
  query?: Record<string, any>; // Parameters to be appended to the URL
  options?: Record<string, any>; // Axios request configuration options
  securityUrl?: boolean; // Flag to indicate if the URL is secure
  baseType?: BaseType; // <--- new
  isImport?: boolean;
}

const Methods = {
  GET: "get",
  POST: "post",
  PUT: "put",
  DELETE: "delete",
};
// Track the status of token refresh
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const redirectToLogin = () => {
  const path = window.location.pathname;
  if (!path.includes('/signin') && !path.includes('/register')) {
    window.location.href = '/signin';
  }
};

axios.interceptors.request.use(
  (config: any) => {
    const securityToken = localStorage.getItem("securityToken");
    const authToken = localStorage.getItem("authToken");
    const apiKey = import.meta.env.VITE_API_KEY;

    // const accessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJodHRwOi8vc2NoZW1hcy54bWxzb2FwLm9yZy93cy8yMDA1LzA1L2lkZW50aXR5L2NsYWltcy9uYW1lIjoiQWRtaW4iLCJqdGkiOiI4NDYzYTdkYi1iZjdkLTQzYTAtOTY3NC03OTI0ZWQ0OGVkMmUiLCJodHRwOi8vc2NoZW1hcy54bWxzb2FwLm9yZy93cy8yMDA1LzA1L2lkZW50aXR5L2NsYWltcy9lbWFpbGFkZHJlc3MiOiJTbWFydHBwc0BnbWFpbDEyMy5jb20iLCJzdWIiOiJBZG1pbiIsInVzZXJJZCI6IkdIdnpOYnMrUERYa09uZ1NmM3k3RXZHWlNJVmh4bTBwcnFodm13RjRxWGNDcFNSRVJUNnBhQVpTU2V1RmNyTHkiLCJyb2xlIjoiQWRtaW4iLCJleHAiOjE3NjczMDA3ODAsImlzcyI6ImxvY2FsaG9zdDo0NDM1MiIsImF1ZCI6ImxvY2FsaG9zdDo0NDM1MiJ9.7Dq_m0_AYKJMgDjUwHSCZfFYEIJkMCqVkhJqdOjtkbU';

    // console.log("accessToken", accessToken);

    const updatedHeaders = {
      ...config.headers,
      Accept: "application/json",
      "Access-Control-Allow-Origin": "*",
    };

    // Match legacy Customer UI: auth via `token` header only (not Authorization).
    // Authorization triggers CORS preflight; API CORS allows token/apiKey but not Bearer.
    const activeToken = authToken || securityToken;
    if (activeToken) {
      updatedHeaders.token = `${activeToken}`;
    }

    if (apiKey) {
      updatedHeaders.apiKey = apiKey;
    }

    return {
      ...config,
      headers: updatedHeaders,
    };
  },
  (error) => Promise.reject(error),
);

const clearAuthStorage = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('loginCustomer');
  localStorage.removeItem('securityToken');
  localStorage.removeItem('refreshToken');
};

axios.interceptors.response.use(
  async (response: AxiosResponse) => {
    // Same as legacy: many APIs return HTTP 200 with code 401/403 for expired/invalid session
    const data = response?.data as { code?: number } | undefined;
    const c = data?.code;
    if (c === 401 || c === 403) {
      clearAuthStorage();
      redirectToLogin();
      return Promise.reject(new Error('Session expired'));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        clearAuthStorage();
        redirectToLogin();
        return Promise.reject(error);
      }

      isRefreshing = true;
      try {
        const accessToken = localStorage.getItem('securityToken');
        const body = { accessToken, refreshToken };
        const fullUrl = API_URL.base + 'auth-user/refresh-token';
        const refreshTokenData = await axios.post(fullUrl, body);

        if (refreshTokenData.data?.apiStatus) {
          const newAccessToken = refreshTokenData?.data?.apiData?.accessToken;
          const newRefreshToken = refreshTokenData?.data?.apiData?.refreshToken;

          if (newAccessToken) {
            localStorage.setItem('authToken', newAccessToken);
            localStorage.setItem('securityToken', newAccessToken);
            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken);
            }
            processQueue(null, newAccessToken);
            return axios(originalRequest);
          }
        }

        processQueue(new Error('Refresh failed'), null);
        clearAuthStorage();
        redirectToLogin();
        return Promise.reject(error);
      } catch (err) {
        processQueue(err, null);
        localStorage.clear();
        redirectToLogin();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 403) {
      clearAuthStorage();
      redirectToLogin();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);


const hasRequestBody = (body: unknown): body is Record<string, unknown> | FormData | string =>
  body !== undefined && body !== null;

const makeHttpRequest = async (
  method: "get" | "post" | "put" | "delete",
  url: string,
  body?: unknown,
  options: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  try {
    const isFormData = body instanceof FormData;
    const sendBody = hasRequestBody(body);

    const headers: Record<string, string> = {};
    if (isFormData) {
      headers['Content-Type'] = 'multipart/form-data';
    } else if (sendBody) {
      headers['Content-Type'] = 'application/json';
    }

    const mergedHeaders = { ...headers, ...options.headers };

    const response = await axios({
      method,
      url,
      ...(sendBody ? { data: body } : {}),
      headers: mergedHeaders,
      ...options,
    });

    return response.data;
  } catch (error: any) {
    return error.response;
  }
};

const makeHttpRequestForImport = async (
  method: "get" | "post" | "put" | "delete",
  url: string,
  body?: unknown,
  options: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  try {
    const isFormData = body instanceof FormData;
    const sendBody = hasRequestBody(body);

    const headers: Record<string, string> = {};
    if (!isFormData && sendBody) {
      headers['Content-Type'] = 'application/json';
    }

    const mergedHeaders = { ...headers, ...options.headers };

    const response = await axios({
      method,
      url,
      ...(sendBody ? { data: body } : {}),
      headers: mergedHeaders,
      ...options,
    });

    return response.data;
  } catch (error: any) {
    return error.response;
  }
};

const makeAPICall = async ({
  methodName,
  apiUrl,
  body,
  params,
  query,
  options,
  baseType = "default",
  isImport = false,
}: APICallParams): Promise<AxiosResponse | undefined> => {
  const baseMap: Record<BaseType, string> = {
    default: API_URL.base || "",

  };
  // let fullUrl = securityUrl ? API_URL.baseForSecurity + apiUrl : API_URL.base + apiUrl; // Use a new variable `fullUrl`
  let fullUrl = baseMap[baseType] + apiUrl;

  if (params) {
    fullUrl += `/${params}`;
  }
  if (query) {
    const queryString = new URLSearchParams(query).toString();
    if (queryString) {
      fullUrl += `?${queryString}`;
    }
  }

  switch (methodName) {
    case Methods.GET:
      return makeHttpRequest('get', fullUrl, undefined, options);
    case Methods.POST:
      return isImport
        ? makeHttpRequestForImport("post", fullUrl, body, options)
        : makeHttpRequest("post", fullUrl, body, options);
    case Methods.PUT:
      return makeHttpRequest("put", fullUrl, body, options);
    case Methods.DELETE:
      // For DELETE, axios expects the body to be in the config object
      return makeHttpRequest("delete", fullUrl, body, {
        ...options,
        data: body,
      });
    default:
      return Promise.reject(new Error("Invalid methodName"));
  }
};

const service = {
  Methods,
  API_URL,
  makeAPICall,
};

export default service;
